import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

/**
 * Custom hook for debouncing a value
 * @param value - The value to debounce
 * @param delay - The delay in milliseconds
 * @returns The debounced value
 */
export const useDebounce = <T>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

/**
 * Custom hook for debouncing a callback function
 * @param callback - The callback function to debounce
 * @param delay - The delay in milliseconds
 * @returns The debounced callback function
 */
export const useDebouncedCallback = <T extends (...args: any[]) => any>(
  callback: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const debouncedCallback = useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay]
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedCallback;
};

/**
 * Custom hook for memoizing filtered/transformed data
 * Useful for large lists that need filtering or sorting
 * @param data - The source data array
 * @param filterFn - The filter function
 * @param deps - Additional dependencies for re-computation
 * @returns The filtered data
 */
export const useFilteredData = <T>(
  data: T[],
  filterFn: (item: T) => boolean,
  deps: any[] = []
): T[] => {
  return useMemo(() => {
    if (!data || data.length === 0) return [];
    return data.filter(filterFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, ...deps]);
};

/**
 * Custom hook for virtualization-friendly pagination
 * Returns only the visible items based on scroll position
 * @param data - The full data array
 * @param visibleCount - Number of items to show at once
 * @param bufferCount - Extra items to render above/below for smooth scrolling
 */
export const useVirtualList = <T>(
  data: T[],
  visibleCount: number = 50,
  bufferCount: number = 10
) => {
  const [startIndex, setStartIndex] = useState(0);

  const visibleData = useMemo(() => {
    const start = Math.max(0, startIndex - bufferCount);
    const end = Math.min(data.length, startIndex + visibleCount + bufferCount);
    return data.slice(start, end);
  }, [data, startIndex, visibleCount, bufferCount]);

  const handleScroll = useCallback(
    (scrollTop: number, itemHeight: number) => {
      const newStartIndex = Math.floor(scrollTop / itemHeight);
      setStartIndex(newStartIndex);
    },
    []
  );

  return {
    visibleData,
    handleScroll,
    totalCount: data.length,
    startIndex,
  };
};

/**
 * Custom hook for lazy loading data in chunks
 * @param fetchFn - Function to fetch data
 * @param chunkSize - Number of items per chunk
 */
export const useLazyLoad = <T>(
  fetchFn: (offset: number, limit: number) => Promise<T[]>,
  chunkSize: number = 50
) => {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const offsetRef = useRef(0);

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;

    setLoading(true);
    try {
      const newData = await fetchFn(offsetRef.current, chunkSize);
      if (newData.length < chunkSize) {
        setHasMore(false);
      }
      setData((prev) => [...prev, ...newData]);
      offsetRef.current += chunkSize;
    } catch (error) {
      console.error('Error loading more data:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchFn, chunkSize, loading, hasMore]);

  const reset = useCallback(() => {
    setData([]);
    offsetRef.current = 0;
    setHasMore(true);
  }, []);

  return { data, loading, hasMore, loadMore, reset };
};

export default {
  useDebounce,
  useDebouncedCallback,
  useFilteredData,
  useVirtualList,
  useLazyLoad,
};
